/*
  FeedNews.jsx — Full component with fixes:
  1. CORS: use relative paths via proxy (Vite/CRA) — set Hoot() to return "/" in dev
  2. Modal fix: removed "contain: style" from .fn-root (was blocking Bootstrap portals)
  3. Design fix: fn-bg-wrap no longer forces min-height/radius that distorts layout
  4. Composer click: guaranteed to fire setShowCreate(true) with e.preventDefault()
*/

import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { Modal, Spinner } from "react-bootstrap"; 
import Mentions from "@rc-component/mentions";
import io from "socket.io-client";
import Hoot from "../Hoot";

import {
  IoHeartOutline, IoHeart,
  IoChatbubbleOutline, IoChatbubble,
  IoShareSocialOutline,
  IoPlaySharp, IoPauseSharp,
  IoPlaySkipForwardSharp, IoPlaySkipBackSharp,
  IoVolumeMediumSharp, IoVolumeMuteSharp, IoVolumeHighSharp, IoVolumeLowSharp,
  IoExpandSharp,
  IoSettingsSharp,
  IoCalendarOutline,
  IoTrashOutline,
  IoCloseSharp,
  IoImageOutline,
  IoVideocamOutline,
  IoEllipsisHorizontal,
  IoLocationOutline,
  IoTimeOutline,
  IoPeopleOutline,
  IoAddSharp,
  IoSendSharp,
  IoCheckmarkSharp,
  IoReturnDownForwardOutline,
  IoSpeedometerOutline,
  IoLayers,
  IoRefreshSharp,
} from "react-icons/io5";

/* ══════════════════════════════════════════════════════════════
   UTILITIES
══════════════════════════════════════════════════════════════ */
const fmtRelative = (iso) => {
  try {
    const diff = Math.floor((Date.now() - new Date(iso)) / 1000);
    if (diff < 60) return "agora";
    if (diff < 3600) return `${Math.floor(diff / 60)}min`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
    return new Date(iso).toLocaleDateString("pt-PT", { day: "2-digit", month: "short" });
  } catch { return ""; }
};

const fmtTime = (s) => {
  if (!s || isNaN(s) || s < 0) return "0:00";
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = Math.floor(s % 60);
  if (h > 0) return `${h}:${String(m).padStart(2,"0")}:${String(ss).padStart(2,"0")}`;
  return `${m}:${String(ss).padStart(2,"0")}`;
};

const avatarColor = (s = "") => {
  const palette = ["var(--ed-purple)","#7c3aed","#0ea5e9","#10b981","#f59e0b","#ec4899","#06b6d4","#8b5cf6"];
  let h = 0;
  for (let i = 0; i < s.length; i++) h = s.charCodeAt(i) + ((h << 5) - h);
  return palette[Math.abs(h) % palette.length];
};

const ytId = (url = "") => {
  const m = url?.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([a-zA-Z0-9_-]{11})/);
  return m ? m[1] : null;
};

const videoBlobCache = new Map();
const fetchVideoBlob = async (src) => {
  if (videoBlobCache.has(src)) return videoBlobCache.get(src);
  const resp = await fetch(src);
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  videoBlobCache.set(src, url);
  return url;
};

/* ══════════════════════════════════════════════════════════════
   AVATAR
══════════════════════════════════════════════════════════════ */
const Avatar = ({ name = "", pic, size = 40, online = false }) => {
  const base = {
    width: size, height: size, borderRadius: "50%", flexShrink: 0,
    objectFit: "cover", display: "block",
    boxShadow: "0 0 0 2.5px #fff, 0 2px 8px rgba(0,0,0,.10)",
  };
  return (
    <div style={{ position: "relative", flexShrink: 0, width: size, height: size }}>
      {pic
        ? <img src={pic} alt={name} style={base} />
        : <div style={{
            ...base, display: "grid", placeItems: "center",
            background: `linear-gradient(145deg, ${avatarColor(name)}, ${avatarColor(name + "2")})`,
            color: "#fff", fontWeight: 800, fontSize: size * 0.37,
            letterSpacing: "-0.5px",
          }}>{(name || "?").charAt(0).toUpperCase()}</div>
      }
      {online && (
        <span style={{
          position: "absolute", bottom: 1, right: 1,
          width: size * 0.27, height: size * 0.27,
          borderRadius: "50%", background: "#22c55e",
          border: "2px solid #fff",
        }} />
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   GLOBAL CSS — ALL SCOPED UNDER .fn-root
   FIXES:
   - removed "contain: style" (was blocking Bootstrap modal portals)
   - fn-bg-wrap no longer forces min-height/radius that distorts layout
══════════════════════════════════════════════════════════════ */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Figtree:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,400&display=swap');

.fn-root {
  --fn-p: var(--ed-purple);
  --fn-p2: #7c3aed;
  --fn-p3: #a855f7;
  --fn-pg: linear-gradient(135deg,var(--ed-purple),#7c3aed);
  --fn-pg2: linear-gradient(135deg,var(--ed-purple) 0%,#a855f7 100%);
  --fn-p-glow: rgba(96,81,255,.2);
  --fn-p-tint: rgba(96,81,255,.08);
  --fn-bg: #eef0f8;
  --fn-bg2: #f7f8fc;
  --fn-card: #ffffff;
  --fn-border: #e2e7f4;
  --fn-border2: rgba(96,81,255,.14);
  --fn-text: #0c1225;
  --fn-text2: #1e2d47;
  --fn-muted: #637087;
  --fn-muted2: #9aaabb;
  --fn-sh1: 0 1px 3px rgba(15,23,42,.05),0 4px 16px rgba(15,23,42,.05);
  --fn-sh2: 0 4px 24px rgba(15,23,42,.09),0 1px 4px rgba(15,23,42,.04);
  --fn-sh3: 0 12px 48px rgba(15,23,42,.14),0 4px 16px rgba(15,23,42,.06);
  --fn-r: 18px;
  --fn-r2: 14px;
  --fn-r3: 10px;
  --fn-dur: .18s;

  font-family: 'Figtree', 'Montserrat', system-ui, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: var(--fn-text);
  /* FIX: removed "isolation: isolate" and "contain: style"
     — these were preventing Bootstrap modal portals from rendering correctly */
}

/* FIX: fn-bg-wrap no longer forces min-height:60vh or border-radius
   that was distorting the layout inside the dashboard */
.fn-root .fn-bg-wrap {
  background: var(--fn-bg);
  padding: 0 0 8px;
}

.fn-root *, .fn-root *::before, .fn-root *::after {
  box-sizing: border-box;
}

.fn-root p, .fn-root h1, .fn-root h2, .fn-root h3,
.fn-root h4, .fn-root h5, .fn-root h6, .fn-root ul,
.fn-root ol, .fn-root li { margin: 0; padding: 0; }

/* ── layout ── */
.fn-root .fn-layout {
  display: grid;
  grid-template-columns: minmax(0,1fr) 292px;
  gap: 20px;
  max-width: 1020px;
  margin: 0 auto;
  padding: 20px 16px 72px;
}
@media(max-width:800px){
  .fn-root .fn-layout { grid-template-columns: 1fr; }
  .fn-root .fn-sidebar { display: none; }
}

/* ── cards base ── */
.fn-root .fn-card {
  background: var(--fn-card);
  border-radius: var(--fn-r);
  border: 1px solid var(--fn-border);
  box-shadow: var(--fn-sh1);
  transition: box-shadow var(--fn-dur);
}
.fn-root .fn-card:hover { box-shadow: var(--fn-sh2); }

/* ── composer ── */
.fn-root .fn-composer { padding: 14px 16px 10px; margin-bottom: 14px; }
.fn-root .fn-cmp-row {
  display: flex; gap: 11px; align-items: center;
  cursor: pointer; padding-bottom: 10px;
  /* FIX: ensure the row itself is always clickable */
  position: relative; z-index: 1;
}
.fn-root .fn-cmp-fake {
  flex: 1; background: var(--fn-bg); border: 1.5px solid var(--fn-border);
  border-radius: 999px; padding: 10px 18px; font-size: .875rem;
  color: var(--fn-muted); font-family: inherit; font-weight: 500;
  transition: border-color var(--fn-dur),color var(--fn-dur);
  /* FIX: allow pointer events so hover effect works, click bubbles to parent */
  pointer-events: none;
}
.fn-root .fn-cmp-row:hover .fn-cmp-fake { border-color: var(--fn-p); color: var(--fn-text2); }
.fn-root .fn-cmp-actions {
  display: flex; gap: 0; border-top: 1px solid var(--fn-border); padding-top: 6px;
}
.fn-root .fn-cmp-act {
  display: flex; align-items: center; gap: 7px; padding: 7px 13px;
  border-radius: var(--fn-r3); border: none; background: transparent;
  color: var(--fn-muted); font-size: .8rem; font-weight: 600; cursor: pointer;
  font-family: inherit; transition: all var(--fn-dur);
}
.fn-root .fn-cmp-act:hover { background: var(--fn-p-tint); color: var(--fn-p); }
.fn-root .fn-cmp-act svg { width: 18px; height: 18px; }

/* ── post card ── */
.fn-root .fn-post {
  margin-bottom: 14px; overflow: hidden;
  animation: fnPostIn .32s cubic-bezier(.16,1,.3,1) both;
}
@keyframes fnPostIn {
  from { opacity:0; transform: translateY(10px) scale(.99); }
  to   { opacity:1; transform: translateY(0) scale(1); }
}
.fn-root .fn-post-header {
  display: flex; align-items: flex-start;
  justify-content: space-between; padding: 15px 17px 0;
}
.fn-root .fn-post-meta { display: flex; gap: 11px; align-items: center; }
.fn-root .fn-post-name {
  font-weight: 700; font-size: .9rem; color: var(--fn-text);
  display: flex; align-items: center; gap: 6px; line-height: 1.2;
}
.fn-root .fn-post-time { font-size: .72rem; color: var(--fn-muted2); font-weight: 500; margin-top: 2px; }
.fn-root .fn-post-body {
  padding: 11px 17px 4px; font-size: .9rem;
  color: var(--fn-text2); line-height: 1.68;
}
.fn-root .fn-post-body p { margin: 0 0 4px; }
.fn-root .fn-post-body a { color: var(--fn-p); text-decoration: none; }
.fn-root .fn-post-body a:hover { text-decoration: underline; }
.fn-root .fn-post-body strong { color: var(--fn-text); }

/* ── image grid ── */
.fn-root .fn-img-grid { margin: 10px 0 0; max-height: 560px; overflow: hidden; }
.fn-root .fn-img-grid .react-fb-image-grid__img { border-radius: 0 !important; }
.fn-root .fn-img-grid * { border-radius: 0 !important; cursor: pointer; }

/* ── youtube embed ── */
.fn-root .fn-yt-box {
  position: relative; padding-top: 56.25%; background: #000; margin: 10px 0 0;
}
.fn-root .fn-yt-box iframe { position: absolute; inset: 0; width: 100%; height: 100%; border: none; }

/* ── event card ── */
.fn-root .fn-event-block {
  margin: 10px 16px 2px; border-radius: var(--fn-r2); overflow: hidden;
  display: flex; border: 1px solid rgba(245,158,11,.22);
  background: linear-gradient(135deg,rgba(245,158,11,.06),rgba(251,191,36,.03));
}
.fn-root .fn-event-datecol {
  background: linear-gradient(180deg,#f59e0b,#d97706);
  padding: 14px 16px; display: flex; flex-direction: column;
  align-items: center; justify-content: center; min-width: 72px;
  color: #fff; flex-shrink: 0;
}
.fn-root .fn-event-mon { font-size: .62rem; font-weight: 800; text-transform: uppercase; letter-spacing: 1px; opacity: .9; }
.fn-root .fn-event-day { font-size: 2rem; font-weight: 900; line-height: 1; }
.fn-root .fn-event-dow { font-size: .6rem; font-weight: 700; text-transform: uppercase; opacity: .75; margin-top: 1px; }
.fn-root .fn-event-info { padding: 12px 14px; flex: 1; }
.fn-root .fn-event-name { font-weight: 800; font-size: .9rem; color: var(--fn-text); margin-bottom: 5px; }
.fn-root .fn-event-row {
  display: flex; align-items: center; gap: 5px;
  font-size: .77rem; color: var(--fn-muted); font-weight: 500; margin-bottom: 2px;
}
.fn-root .fn-event-row svg { width: 14px; height: 14px; color: #d97706; flex-shrink: 0; }

/* ── actions bar ── */
.fn-root .fn-actions {
  display: flex; align-items: center; gap: 2px;
  padding: 7px 10px; border-top: 1px solid var(--fn-border);
}
.fn-root .fn-act {
  display: flex; align-items: center; gap: 6px;
  padding: 8px 13px; border-radius: var(--fn-r3); border: none;
  background: transparent; color: var(--fn-muted);
  font-size: .82rem; font-weight: 600; cursor: pointer;
  font-family: inherit; transition: all var(--fn-dur); position: relative;
}
.fn-root .fn-act:hover { background: var(--fn-p-tint); color: var(--fn-p); }
.fn-root .fn-act.liked { color: #ef4444; }
.fn-root .fn-act.liked:hover { background: rgba(239,68,68,.08); }
.fn-root .fn-act.commented { color: var(--fn-p); }
.fn-root .fn-act svg { width: 18px; height: 18px; transition: transform .15s; }
.fn-root .fn-act:active svg { transform: scale(.88); }
.fn-root .fn-act-n {
  font-size: .74rem; font-weight: 700;
  background: var(--fn-p-tint); color: var(--fn-p);
  padding: 1px 7px; border-radius: 999px; min-width: 20px; text-align: center;
}
.fn-root .fn-act.liked .fn-act-n { background: rgba(239,68,68,.1); color: #ef4444; }
.fn-root .fn-act-sep { width: 1px; height: 18px; background: var(--fn-border); margin: 0 3px; flex-shrink: 0; }

/* ── pill tag ── */
.fn-root .fn-pill {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 3px 9px; border-radius: 999px;
  font-size: .69rem; font-weight: 700; letter-spacing: .2px;
}
.fn-root .fn-pill-event {
  background: rgba(245,158,11,.12); color: #d97706;
  border: 1px solid rgba(245,158,11,.22);
}

/* ── dots btn ── */
.fn-root .fn-dots {
  width: 32px; height: 32px; border-radius: 50%; border: none;
  background: transparent; color: var(--fn-muted);
  cursor: pointer; display: flex; align-items: center; justify-content: center;
  transition: all var(--fn-dur);
}
.fn-root .fn-dots:hover { background: var(--fn-bg); color: var(--fn-text); }
.fn-root .fn-dots svg { width: 18px; height: 18px; }

/* ══════════════════════════════════════════════════════
   VIDEO PLAYER (scoped)
══════════════════════════════════════════════════════ */
.fn-root .fn-player {
  position: relative; background: #000; overflow: hidden;
  cursor: none; -webkit-user-select: none; user-select: none;
}
.fn-root .fn-player.ctrl-vis { cursor: default; }
.fn-root .fn-player video {
  width: 100%; display: block; max-height: 490px;
  object-fit: contain; transition: opacity .3s;
}
.fn-root .fn-player.cinema video { max-height: 100vh; }

.fn-root .fn-cinema-left {
  position: absolute; top: 0; left: 0; bottom: 0; width: 40px;
  background: linear-gradient(to right,rgba(0,0,0,.35),transparent);
  pointer-events: none; z-index: 2; opacity: 0; transition: opacity .3s;
}
.fn-root .fn-cinema-right {
  position: absolute; top: 0; right: 0; bottom: 0; width: 40px;
  background: linear-gradient(to left,rgba(0,0,0,.35),transparent);
  pointer-events: none; z-index: 2; opacity: 0; transition: opacity .3s;
}
.fn-root .fn-player.playing .fn-cinema-left,
.fn-root .fn-player.playing .fn-cinema-right { opacity: 1; }

.fn-root .fn-center-flash {
  position: absolute; inset: 0; display: flex;
  align-items: center; justify-content: center;
  pointer-events: none; z-index: 10;
}
.fn-root .fn-flash-ring {
  width: 72px; height: 72px; border-radius: 50%;
  background: rgba(0,0,0,.5);
  display: flex; align-items: center; justify-content: center;
  color: #fff; opacity: 0; transform: scale(.6); transition: none;
}
.fn-root .fn-flash-ring.active {
  animation: fnFlashPop .55s cubic-bezier(.4,0,.2,1) forwards;
}
@keyframes fnFlashPop {
  0%   { opacity:0; transform:scale(.6); }
  25%  { opacity:1; transform:scale(1.1); }
  60%  { opacity:1; transform:scale(1); }
  100% { opacity:0; transform:scale(1.2); }
}

.fn-root .fn-buffer {
  position: absolute; top: 50%; left: 50%;
  transform: translate(-50%,-50%);
  width: 46px; height: 46px; border-radius: 50%;
  border: 3px solid rgba(255,255,255,.15);
  border-top-color: var(--fn-p);
  animation: fnSpin .75s linear infinite;
  z-index: 6; pointer-events: none;
}
@keyframes fnSpin { to { transform: translate(-50%,-50%) rotate(360deg); } }

.fn-root .fn-controls {
  position: absolute; bottom: 0; left: 0; right: 0;
  background: linear-gradient(
    transparent 0%,rgba(0,0,0,.06) 30%,
    rgba(0,0,0,.42) 65%,rgba(0,0,0,.78) 88%,rgba(0,0,0,.88) 100%
  );
  padding: 48px 14px 10px;
  opacity: 0; transition: opacity .3s ease;
  pointer-events: none; z-index: 8;
}
.fn-root .fn-controls.vis { opacity: 1; pointer-events: all; }

.fn-root .fn-seekbar {
  position: relative; width: 100%; height: 16px;
  display: flex; align-items: center; cursor: pointer; margin-bottom: 4px;
}
.fn-root .fn-seekbar-track {
  position: absolute; inset: 0; margin: auto 0; height: 3px;
  background: rgba(255,255,255,.22); border-radius: 99px;
  transition: height .15s,margin .15s;
}
.fn-root .fn-seekbar:hover .fn-seekbar-track { height: 5px; }
.fn-root .fn-seekbar-buf {
  position: absolute; top: 0; left: 0; height: 100%;
  background: rgba(255,255,255,.32); border-radius: 99px;
  pointer-events: none; transition: width .3s linear;
}
.fn-root .fn-seekbar-prog {
  position: absolute; top: 0; left: 0; height: 100%;
  background: var(--fn-pg); border-radius: 99px; pointer-events: none;
}
.fn-root .fn-seekbar-thumb {
  position: absolute; top: 50%;
  width: 15px; height: 15px; border-radius: 50%; background: #fff;
  transform: translateY(-50%) translateX(-50%);
  opacity: 0; transition: opacity .15s;
  box-shadow: 0 0 0 3px rgba(96,81,255,.5); pointer-events: none;
}
.fn-root .fn-seekbar:hover .fn-seekbar-thumb { opacity: 1; }
.fn-root .fn-seek-tip {
  position: absolute; bottom: 20px;
  background: rgba(20,20,30,.88); backdrop-filter: blur(6px);
  color: #fff; font-size: .7rem; font-weight: 700;
  padding: 3px 8px; border-radius: 6px;
  white-space: nowrap; transform: translateX(-50%);
  pointer-events: none; font-family: 'Figtree',sans-serif;
  box-shadow: 0 2px 8px rgba(0,0,0,.3);
}

.fn-root .fn-ctrl-row { display: flex; align-items: center; gap: 2px; }
.fn-root .fn-cbtn {
  width: 38px; height: 38px; border-radius: 50%; border: none;
  background: transparent; color: #fff; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: background .12s,transform .1s; flex-shrink: 0;
}
.fn-root .fn-cbtn:hover { background: rgba(255,255,255,.16); }
.fn-root .fn-cbtn:active { transform: scale(.9); }
.fn-root .fn-cbtn svg { width: 20px; height: 20px; }
.fn-root .fn-cbtn.sm svg { width: 17px; height: 17px; }
.fn-root .fn-cbtn.lg svg { width: 24px; height: 24px; }

.fn-root .fn-vol { display: flex; align-items: center; gap: 0; }
.fn-root .fn-vol-slide-wrap {
  max-width: 0; overflow: hidden;
  transition: max-width .22s ease; display: flex; align-items: center;
}
.fn-root .fn-vol:hover .fn-vol-slide-wrap,
.fn-root .fn-vol-slide-wrap:focus-within { max-width: 80px; }
.fn-root .fn-vol-input {
  -webkit-appearance: none; appearance: none;
  width: 70px; height: 3px; border-radius: 99px;
  background: rgba(255,255,255,.3); outline: none; cursor: pointer;
  accent-color: var(--fn-p);
}
.fn-root .fn-vol-input::-webkit-slider-thumb {
  -webkit-appearance: none; width: 13px; height: 13px;
  border-radius: 50%; background: #fff; cursor: pointer;
  box-shadow: 0 1px 4px rgba(0,0,0,.4);
}

.fn-root .fn-time {
  color: rgba(255,255,255,.9); font-size: .78rem;
  font-weight: 600; white-space: nowrap; padding: 0 6px;
  font-family: 'Figtree',sans-serif;
}
.fn-root .fn-time-sep { color: rgba(255,255,255,.45); margin: 0 1px; }

.fn-root .fn-speed-menu {
  position: absolute; bottom: 54px; right: 14px;
  background: rgba(15,15,25,.92); backdrop-filter: blur(12px);
  border: 1px solid rgba(255,255,255,.12);
  border-radius: 12px; overflow: hidden; min-width: 120px;
  box-shadow: 0 8px 32px rgba(0,0,0,.5); z-index: 20;
  animation: fnMenuIn .15s ease;
}
@keyframes fnMenuIn { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:none; } }
.fn-root .fn-speed-item {
  display: flex; align-items: center; justify-content: space-between;
  padding: 9px 14px; color: rgba(255,255,255,.85);
  font-size: .8rem; font-weight: 600; cursor: pointer;
  font-family: 'Figtree',sans-serif; gap: 12px; transition: background .1s;
}
.fn-root .fn-speed-item:hover { background: rgba(255,255,255,.12); }
.fn-root .fn-speed-item.active { color: var(--fn-p); background: rgba(96,81,255,.15); }
.fn-root .fn-speed-item svg { width: 14px; height: 14px; flex-shrink: 0; }

.fn-root .fn-qual-menu {
  position: absolute; bottom: 54px; right: 60px;
  background: rgba(15,15,25,.92); backdrop-filter: blur(12px);
  border: 1px solid rgba(255,255,255,.12);
  border-radius: 12px; overflow: hidden; min-width: 130px;
  box-shadow: 0 8px 32px rgba(0,0,0,.5); z-index: 20;
  animation: fnMenuIn .15s ease;
}

.fn-root .fn-menu-hdr {
  padding: 8px 14px 5px;
  font-size: .7rem; font-weight: 700; color: rgba(255,255,255,.4);
  text-transform: uppercase; letter-spacing: .8px;
  font-family: 'Figtree',sans-serif; border-bottom: 1px solid rgba(255,255,255,.08);
}

.fn-root .fn-badge {
  display: inline-block; padding: 1px 7px; border-radius: 99px;
  font-size: .64rem; font-weight: 800; background: var(--fn-pg); color: #fff;
  letter-spacing: .2px;
}

/* ── comments ── */
.fn-root .fn-comments { border-top: 1px solid var(--fn-border); padding: 14px 16px; }
.fn-root .fn-comment-header {
  display: flex; align-items: center; justify-content: space-between; margin-bottom: 13px;
}
.fn-root .fn-comment-title {
  font-size: .85rem; font-weight: 800; color: var(--fn-text);
  display: flex; align-items: center; gap: 8px;
}
.fn-root .fn-comment-cnt {
  background: var(--fn-pg); color: #fff; border-radius: 999px;
  font-size: .72rem; font-weight: 800; padding: 1px 8px;
}
.fn-root .fn-comment-compose {
  display: flex; gap: 10px; align-items: flex-start; margin-bottom: 15px;
}
.fn-root .fn-comment-box {
  flex: 1; background: var(--fn-bg); border: 1.5px solid var(--fn-border);
  border-radius: var(--fn-r2); padding: 10px 14px;
  display: flex; flex-direction: column; gap: 8px;
  transition: border-color var(--fn-dur),box-shadow var(--fn-dur);
}
.fn-root .fn-comment-box:focus-within {
  border-color: var(--fn-p); box-shadow: 0 0 0 3px var(--fn-p-glow);
}
.fn-root .fn-mentions-input {
  border: none !important; background: transparent !important;
  font-family: 'Figtree',sans-serif !important;
  font-size: .86rem !important; color: var(--fn-text) !important;
  resize: none !important; outline: none !important; width: 100% !important;
  min-height: 36px !important; line-height: 1.5 !important;
}
.fn-root .fn-comment-foot {
  display: flex; align-items: center; justify-content: space-between;
}
.fn-root .fn-send-btn {
  display: flex; align-items: center; gap: 6px;
  background: var(--fn-pg); color: #fff; border: none;
  border-radius: var(--fn-r3); padding: 7px 14px;
  font-size: .8rem; font-weight: 700; cursor: pointer;
  font-family: inherit; transition: opacity var(--fn-dur),transform var(--fn-dur);
}
.fn-root .fn-send-btn:hover { opacity: .88; transform: translateY(-1px); }
.fn-root .fn-send-btn:disabled { opacity: .5; cursor: not-allowed; transform: none; }
.fn-root .fn-send-btn svg { width: 14px; height: 14px; }
.fn-root .fn-cancel-btn {
  background: none; border: none; color: var(--fn-muted);
  font-size: .78rem; font-weight: 600; cursor: pointer;
  font-family: inherit; padding: 6px 10px; border-radius: 8px;
  transition: all var(--fn-dur);
}
.fn-root .fn-cancel-btn:hover { background: var(--fn-bg); color: var(--fn-text); }

.fn-root .fn-cmt {
  display: flex; gap: 10px; margin-bottom: 12px;
  animation: fnCmtIn .2s ease both;
}
@keyframes fnCmtIn { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:none; } }
.fn-root .fn-cmt-bubble {
  flex: 1; background: var(--fn-bg2); border: 1px solid var(--fn-border);
  border-radius: 4px 14px 14px 14px; padding: 10px 13px;
}
.fn-root .fn-cmt-author {
  font-weight: 700; font-size: .82rem; color: var(--fn-text);
  display: inline; margin-right: 6px;
}
.fn-root .fn-cmt-time { font-size: .7rem; color: var(--fn-muted2); font-weight: 500; }
.fn-root .fn-cmt-text {
  margin-top: 4px; font-size: .85rem; color: var(--fn-text2); line-height: 1.55;
}
.fn-root .fn-cmt-actions {
  display: flex; gap: 10px; margin-top: 7px; align-items: center;
}
.fn-root .fn-cmt-act {
  display: flex; align-items: center; gap: 4px; background: none; border: none;
  color: var(--fn-muted); font-size: .74rem; font-weight: 600; cursor: pointer;
  padding: 0; font-family: inherit; transition: color var(--fn-dur);
}
.fn-root .fn-cmt-act:hover { color: var(--fn-p); }
.fn-root .fn-cmt-act.liked { color: #ef4444; }
.fn-root .fn-cmt-act.delete:hover { color: #ef4444; }
.fn-root .fn-cmt-act svg { width: 14px; height: 14px; }

.fn-root .fn-subcmts {
  margin-top: 10px; padding-left: 14px;
  border-left: 2px solid var(--fn-border2);
}
.fn-root .fn-subcmt { display: flex; gap: 8px; margin-bottom: 9px; }
.fn-root .fn-subcmt-bubble {
  flex: 1; background: rgba(96,81,255,.04);
  border: 1px solid var(--fn-border2);
  border-radius: 4px 12px 12px 12px; padding: 8px 12px;
}

/* skeleton */
.fn-root .fn-skel {
  background: linear-gradient(90deg,#e8ecf4 25%,#f3f5fb 50%,#e8ecf4 75%);
  background-size: 200% 100%;
  animation: fnSkelShimmer 1.4s infinite; border-radius: 10px;
}
@keyframes fnSkelShimmer { 0% { background-position:200% 0; } 100% { background-position:-200% 0; } }

/* ── sidebar ── */
.fn-root .fn-sb-card { padding: 16px; margin-bottom: 14px; }
.fn-root .fn-sb-title {
  font-size: .82rem; font-weight: 800; color: var(--fn-text);
  margin-bottom: 12px; display: flex; align-items: center;
  justify-content: space-between; text-transform: uppercase; letter-spacing: .4px;
}
.fn-root .fn-sb-user {
  display: flex; gap: 10px; align-items: center;
  padding: 7px 0; border-bottom: 1px solid var(--fn-border);
}
.fn-root .fn-sb-user:last-child { border-bottom: none; }
.fn-root .fn-sb-name { font-size: .83rem; font-weight: 600; color: var(--fn-text); }
.fn-root .fn-sb-role { font-size: .72rem; color: var(--fn-muted2); margin-top: 1px; }

/* ── feed end ── */
.fn-root .fn-feed-end {
  text-align: center; padding: 20px 0 8px; color: var(--fn-muted2);
  font-size: .78rem; font-weight: 600; display: flex;
  align-items: center; justify-content: center; gap: 8px;
}
.fn-root .fn-feed-end::before,
.fn-root .fn-feed-end::after {
  content: ""; flex: 1; height: 1px; background: var(--fn-border);
}

/* ── inline form control ── */
.fn-root .fn-input {
  width: 100%; border: 1.5px solid var(--fn-border); border-radius: var(--fn-r3);
  padding: 9px 13px; font-size: .86rem; font-family: inherit;
  color: var(--fn-text); background: var(--fn-card); outline: none;
  transition: border-color var(--fn-dur),box-shadow var(--fn-dur);
}
.fn-root .fn-input:focus {
  border-color: var(--fn-p); box-shadow: 0 0 0 3px var(--fn-p-glow);
}
.fn-root .fn-grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }

/* ── SimpleEditor ── */
.fn-root .fn-simple-editor,
.fn-modal .fn-simple-editor {
  border: 1.5px solid var(--fn-border,#e2e7f4);
  border-radius: 12px; overflow: hidden;
  background: #fff;
  font-family: 'Figtree',sans-serif;
}
.fn-root .fn-se-toolbar,
.fn-modal .fn-se-toolbar {
  display: flex; gap: 2px; padding: 6px 8px;
  border-bottom: 1px solid var(--fn-border,#e2e7f4);
  background: #fafbfe; flex-wrap: wrap;
}
.fn-root .fn-se-btn,
.fn-modal .fn-se-btn {
  padding: 4px 9px; border-radius: 6px; border: 1px solid transparent;
  background: transparent; cursor: pointer; font-size: .82rem;
  font-family: 'Figtree',sans-serif; color: var(--fn-text,#0c1225);
  line-height: 1.4; transition: all .14s;
}
.fn-root .fn-se-btn:hover,
.fn-modal .fn-se-btn:hover { background: rgba(96,81,255,.1); border-color: rgba(96,81,255,.2); color: var(--ed-purple); }
.fn-root .fn-se-body,
.fn-modal .fn-se-body {
  min-height: 130px; padding: 12px 14px;
  font-size: .9rem; font-family: 'Figtree',sans-serif;
  color: var(--fn-text,#0c1225); line-height: 1.65;
  outline: none; overflow-y: auto; max-height: 320px;
}
.fn-root .fn-se-body:empty::before,
.fn-modal .fn-se-body:empty::before {
  content: attr(data-placeholder);
  color: var(--fn-muted,#637087); pointer-events: none;
}

/* ── rc-mentions overrides ── */
.fn-root .rc-mentions { width: 100%; }
.fn-root .rc-mentions > textarea {
  border: none !important; background: transparent !important;
  font-family: 'Figtree',sans-serif !important; font-size: .86rem !important;
  color: var(--fn-text) !important; resize: none !important; outline: none !important;
  width: 100% !important; min-height: 36px !important; line-height: 1.5 !important;
}

/* ── hidden util ── */
.fn-root .fn-hidden { display: none; }

/* ── image preview thumbs ── */
.fn-root .fn-thumb-row { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 10px; }
.fn-root .fn-thumb {
  position: relative; width: 72px; height: 72px; border-radius: 10px;
  overflow: hidden; border: 1px solid var(--fn-border); flex-shrink: 0;
}
.fn-root .fn-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
.fn-root .fn-thumb-del {
  position: absolute; top: 3px; right: 3px;
  background: rgba(0,0,0,.6); color: #fff; border: none;
  border-radius: 5px; width: 20px; height: 20px;
  display: flex; align-items: center; justify-content: center; cursor: pointer;
}
.fn-root .fn-thumb-del svg { width: 12px; height: 12px; }

/* ── video pill ── */
.fn-root .fn-vid-pill {
  display: flex; align-items: center; gap: 8px;
  padding: 8px 12px; background: var(--fn-p-tint);
  border: 1px solid var(--fn-border2); border-radius: var(--fn-r3);
  margin-top: 8px; font-size: .8rem; font-weight: 600;
}
.fn-root .fn-vid-pill svg { width: 16px; height: 16px; color: var(--fn-p); }

/* ── yt preview in modal ── */
.fn-root .fn-yt-prev {
  margin-top: 10px; border-radius: var(--fn-r2); overflow: hidden;
  border: 1px solid var(--fn-border);
}
.fn-root .fn-yt-prev iframe { width: 100%; height: 240px; border: none; display: block; }

/* ══════════════════════════════════════════════════════
   MODALS
══════════════════════════════════════════════════════ */
.fn-modal .modal-content {
  border-radius: 22px; border: 1px solid #e2e7f4;
  overflow: hidden; font-family: 'Figtree',sans-serif;
  box-shadow: 0 12px 48px rgba(15,23,42,.14);
}
.fn-modal .modal-header {
  background: linear-gradient(135deg,rgba(96,81,255,.09),rgba(124,58,237,.06)),#fff;
  border-bottom: 1px solid #e2e7f4; padding: 16px 20px;
}
.fn-modal .modal-title {
  font-weight: 800; font-size: 1rem; color: #0c1225;
  display: flex; align-items: center; gap: 9px; font-family: 'Figtree',sans-serif;
}
.fn-modal .modal-body { padding: 20px 22px; font-family: 'Figtree',sans-serif; }
.fn-modal .modal-footer {
  border-top: 1px solid #e2e7f4; background: #f7f8fc; padding: 12px 20px;
}

/* type tabs */
.fn-modal .fn-type-tabs { display: flex; gap: 7px; margin-bottom: 16px; }
.fn-modal .fn-type-tab {
  display: flex; align-items: center; gap: 7px;
  border: 1.5px solid #e2e7f4; background: #fff;
  border-radius: 10px; padding: 7px 14px;
  font-size: .8rem; font-weight: 700; cursor: pointer;
  font-family: 'Figtree',sans-serif; color: #637087; transition: all .18s;
}
.fn-modal .fn-type-tab svg { width: 16px; height: 16px; }
.fn-modal .fn-type-tab.active {
  background: linear-gradient(135deg,var(--ed-purple),#7c3aed); color: #fff;
  border-color: transparent; box-shadow: 0 4px 14px rgba(96,81,255,.2);
}

/* event fields */
.fn-modal .fn-event-fields {
  background: rgba(245,158,11,.07); border: 1px solid rgba(245,158,11,.2);
  border-radius: 14px; padding: 14px 16px; margin-bottom: 14px;
}
.fn-modal .fn-event-fields-hdr {
  font-size: .82rem; font-weight: 800; color: #d97706;
  margin-bottom: 10px; display: flex; align-items: center; gap: 7px;
}
.fn-modal .fn-event-fields-hdr svg { width: 16px; height: 16px; }

/* upload zone */
.fn-modal .fn-upload-zone {
  border: 2px dashed #e2e7f4; border-radius: 14px;
  padding: 22px; text-align: center; cursor: pointer;
  color: #637087; font-size: .83rem; font-weight: 500;
  transition: border-color .18s,background .18s;
}
.fn-modal .fn-upload-zone:hover { border-color: var(--ed-purple); background: rgba(96,81,255,.08); }

/* fn-input inside modal */
.fn-modal .fn-input {
  width: 100%; border: 1.5px solid #e2e7f4; border-radius: 10px;
  padding: 9px 13px; font-size: .86rem; font-family: 'Figtree',sans-serif;
  color: #0c1225; background: #fff; outline: none;
  transition: border-color .18s,box-shadow .18s;
}
.fn-modal .fn-input:focus {
  border-color: var(--ed-purple); box-shadow: 0 0 0 3px rgba(96,81,255,.2);
}
.fn-modal .fn-grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.fn-modal .fn-hidden { display: none; }
.fn-modal .fn-thumb-row { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 10px; }
.fn-modal .fn-thumb {
  position: relative; width: 72px; height: 72px; border-radius: 10px;
  overflow: hidden; border: 1px solid #e2e7f4; flex-shrink: 0;
}
.fn-modal .fn-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
.fn-modal .fn-thumb-del {
  position: absolute; top: 3px; right: 3px; background: rgba(0,0,0,.6);
  color: #fff; border: none; border-radius: 5px; width: 20px; height: 20px;
  display: flex; align-items: center; justify-content: center; cursor: pointer;
}
.fn-modal .fn-thumb-del svg { width: 12px; height: 12px; }
.fn-modal .fn-vid-pill {
  display: flex; align-items: center; gap: 8px; padding: 8px 12px;
  background: rgba(96,81,255,.08); border: 1px solid rgba(96,81,255,.14);
  border-radius: 10px; margin-top: 8px; font-size: .8rem; font-weight: 600;
  font-family: 'Figtree',sans-serif;
}
.fn-modal .fn-vid-pill svg { width: 16px; height: 16px; color: var(--ed-purple); }
.fn-modal .fn-yt-prev {
  margin-top: 10px; border-radius: 14px; overflow: hidden; border: 1px solid #e2e7f4;
}
.fn-modal .fn-yt-prev iframe { width: 100%; height: 240px; border: none; display: block; }
.fn-modal .fn-cmp-act {
  display: flex; align-items: center; gap: 7px; padding: 7px 13px;
  border-radius: 10px; border: none; background: transparent;
  color: #637087; font-size: .8rem; font-weight: 600; cursor: pointer;
  font-family: 'Figtree',sans-serif; transition: all .18s;
}
.fn-modal .fn-cmp-act:hover { background: rgba(96,81,255,.08); color: var(--ed-purple); }
.fn-modal .fn-cmp-act svg { width: 18px; height: 18px; }
.fn-modal .fn-send-btn {
  display: flex; align-items: center; gap: 6px;
  background: linear-gradient(135deg,var(--ed-purple),#7c3aed); color: #fff; border: none;
  border-radius: 10px; padding: 7px 14px; font-size: .8rem; font-weight: 700;
  cursor: pointer; font-family: 'Figtree',sans-serif; transition: opacity .18s,transform .18s;
}
.fn-modal .fn-send-btn:hover { opacity: .88; transform: translateY(-1px); }
.fn-modal .fn-send-btn:disabled { opacity: .5; cursor: not-allowed; transform: none; }
.fn-modal .fn-send-btn svg { width: 14px; height: 14px; }
.fn-modal .fn-cancel-btn {
  background: none; border: none; color: #637087; font-size: .78rem; font-weight: 600;
  cursor: pointer; font-family: 'Figtree',sans-serif; padding: 6px 10px;
  border-radius: 8px; transition: all .18s;
}
.fn-modal .fn-cancel-btn:hover { background: #eef0f8; color: #0c1225; }

/* fullscreen video modal */
.fn-vmodal .modal-dialog { max-width: 96vw; margin: 2vh auto; }
.fn-vmodal .modal-content { background: #090910; border: none; border-radius: 16px; overflow: hidden; }
.fn-vmodal .modal-header { background: #090910; border: none; padding: 10px 14px; }
.fn-vmodal .modal-body { padding: 0; }
.fn-vmodal .modal-title { color: rgba(255,255,255,.8); font-size: .88rem; font-weight: 700; font-family: 'Figtree',sans-serif; }

/* rc-mentions dropdown (global — renders in portal) */
.rc-mentions-dropdown {
  border: 1px solid #e2e7f4; border-radius: 12px;
  box-shadow: 0 4px 24px rgba(15,23,42,.09); background: #fff; overflow: hidden; z-index: 9999;
}
.rc-mentions-dropdown-menu-item {
  padding: 9px 14px; font-size: .83rem; font-family: 'Figtree',sans-serif;
  cursor: pointer; display: flex; align-items: center; gap: 8px;
}
.rc-mentions-dropdown-menu-item:hover,
.rc-mentions-dropdown-menu-item-active { background: rgba(96,81,255,.08); color: var(--ed-purple); }
`;

/* ══════════════════════════════════════════════════════════════
   VIDEO PLAYER
══════════════════════════════════════════════════════════════ */
const SPEEDS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];
const QUALITIES = [
  { label: "Auto", value: "auto" },
  { label: "1080p HD", value: "1080" },
  { label: "720p HD", value: "720" },
  { label: "480p", value: "480" },
  { label: "360p", value: "360" },
];

const VideoPlayer = ({ src, onExpand, cinema = false }) => {
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const hideTimerRef = useRef(null);
  const seekDragRef = useRef(false);

  const [playing, setPlaying] = useState(false);
  const [buffering, setBuffering] = useState(true);
  const [progress, setProgress] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [ctrlVis, setCtrlVis] = useState(true);
  const [showFlash, setShowFlash] = useState(false);
  const [flashIcon, setFlashIcon] = useState(null);
  const [speed, setSpeed] = useState(1);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const [showQualMenu, setShowQualMenu] = useState(false);
  const [quality, setQuality] = useState("auto");
  const [seekTip, setSeekTip] = useState(null);
  const [cachedSrc, setCachedSrc] = useState(null);
  const [visible, setVisible] = useState(false);
  const [hadPlayed, setHadPlayed] = useState(false);

  useEffect(() => {
    if (!src) return;
    const isDirectFile = /\.(mp4|webm|mov|ogg|mkv)(\?|$)/i.test(src);
    if (isDirectFile) {
      fetchVideoBlob(src).then(blobUrl => setCachedSrc(blobUrl)).catch(() => setCachedSrc(src));
    } else {
      setCachedSrc(src);
    }
  }, [src]);

  useEffect(() => {
    if (!containerRef.current) return;
    const obs = new IntersectionObserver(([e]) => setVisible(e.isIntersecting), { threshold: 0.5 });
    obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!videoRef.current) return;
    if (!visible && playing) { videoRef.current.pause(); setPlaying(false); }
  }, [visible]);

  const showControls = useCallback(() => {
    setCtrlVis(true);
    clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => { if (playing) setCtrlVis(false); }, 3200);
  }, [playing]);

  useEffect(() => { if (!playing) setCtrlVis(true); else showControls(); }, [playing]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.setAttribute("tabindex", "0");
    const handler = (e) => {
      if (!containerRef.current?.contains(document.activeElement) &&
          document.activeElement !== containerRef.current) return;
      const vid = videoRef.current;
      if (!vid) return;
      switch (e.key) {
        case " ": case "k": e.preventDefault(); togglePlay(); break;
        case "ArrowRight": e.preventDefault(); vid.currentTime = Math.min(vid.duration, vid.currentTime + 10); triggerFlash(<IoPlaySkipForwardSharp size={28} />); break;
        case "ArrowLeft":  e.preventDefault(); vid.currentTime = Math.max(0, vid.currentTime - 10); triggerFlash(<IoPlaySkipBackSharp size={28} />); break;
        case "ArrowUp":    e.preventDefault(); changeVolume(Math.min(1, volume + 0.1)); break;
        case "ArrowDown":  e.preventDefault(); changeVolume(Math.max(0, volume - 0.1)); break;
        case "m": toggleMute(); break;
        case "f": onExpand?.(); break;
        case ">": changeSpeed(Math.min(2, speed + 0.25)); break;
        case "<": changeSpeed(Math.max(0.25, speed - 0.25)); break;
        default: break;
      }
    };
    el.addEventListener("keydown", handler);
    return () => el.removeEventListener("keydown", handler);
  }, [playing, volume, speed]);

  const triggerFlash = (icon) => {
    setFlashIcon(icon);
    setShowFlash(false);
    requestAnimationFrame(() => requestAnimationFrame(() => setShowFlash(true)));
    setTimeout(() => setShowFlash(false), 600);
  };

  const togglePlay = () => {
    const vid = videoRef.current;
    if (!vid) return;
    if (vid.paused) {
      vid.play().then(() => { setPlaying(true); setHadPlayed(true); });
      triggerFlash(<IoPlaySharp size={28} />);
    } else {
      vid.pause(); setPlaying(false);
      triggerFlash(<IoPauseSharp size={28} />);
    }
  };

  const toggleMute = () => {
    const vid = videoRef.current;
    if (!vid) return;
    const next = !muted;
    vid.muted = next; setMuted(next);
  };

  const changeVolume = (v) => {
    const vid = videoRef.current;
    if (!vid) return;
    const clamped = Math.max(0, Math.min(1, v));
    vid.volume = clamped; setVolume(clamped);
    setMuted(clamped === 0); vid.muted = clamped === 0;
  };

  const changeSpeed = (v) => {
    const vid = videoRef.current;
    if (!vid) return;
    vid.playbackRate = v; setSpeed(v);
    triggerFlash(<IoSpeedometerOutline size={24} />);
  };

  const calcSeekRatio = (e, barEl) => {
    const rect = barEl.getBoundingClientRect();
    return Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
  };

  const onSeekbarMouseMove = (e) => {
    const bar = e.currentTarget;
    const ratio = calcSeekRatio(e, bar);
    const t = ratio * duration;
    const rect = bar.getBoundingClientRect();
    setSeekTip({ x: e.clientX - rect.left, time: t });
    if (seekDragRef.current && videoRef.current) {
      videoRef.current.currentTime = t;
      setProgress(ratio); setCurrentTime(t);
    }
  };

  const onSeekbarClick = (e) => {
    if (!videoRef.current) return;
    const ratio = calcSeekRatio(e, e.currentTarget);
    videoRef.current.currentTime = ratio * duration;
  };

  const VolumeIcon = () => {
    if (muted || volume === 0) return <IoVolumeMuteSharp />;
    if (volume < 0.35) return <IoVolumeLowSharp />;
    if (volume < 0.7) return <IoVolumeMediumSharp />;
    return <IoVolumeHighSharp />;
  };

  const playerClass = ["fn-player", playing ? "playing" : "", ctrlVis ? "ctrl-vis" : "", cinema ? "cinema" : ""].filter(Boolean).join(" ");

  return (
    <div
      ref={containerRef}
      className={playerClass}
      onMouseMove={showControls}
      onMouseLeave={() => playing && setCtrlVis(false)}
      onClick={togglePlay}
      tabIndex={0}
    >
      <div className="fn-cinema-left" />
      <div className="fn-cinema-right" />
      <video
        ref={videoRef}
        src={cachedSrc || undefined}
        muted={muted}
        onTimeUpdate={() => {
          if (!videoRef.current) return;
          const vid = videoRef.current;
          setCurrentTime(vid.currentTime);
          setProgress(vid.currentTime / (vid.duration || 1));
          if (vid.buffered.length > 0)
            setBuffered(vid.buffered.end(vid.buffered.length - 1) / (vid.duration || 1));
        }}
        onLoadedMetadata={() => { if (videoRef.current) setDuration(videoRef.current.duration); setBuffering(false); }}
        onWaiting={() => setBuffering(true)}
        onCanPlay={() => setBuffering(false)}
        onPlaying={() => { setPlaying(true); setBuffering(false); }}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false); setCtrlVis(true); setProgress(0); setCurrentTime(0);
          if (videoRef.current) videoRef.current.currentTime = 0;
        }}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={onExpand}
        style={{ maxHeight: cinema ? "100vh" : 490 }}
        preload="metadata"
      />
      {buffering && hadPlayed && <div className="fn-buffer" />}
      {!hadPlayed && (
        <div
          style={{ position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(0,0,0,.28)",zIndex:7,cursor:"pointer" }}
          onClick={(e) => { e.stopPropagation(); togglePlay(); }}
        >
          <div style={{ width:72,height:72,borderRadius:"50%",background:"linear-gradient(135deg,var(--ed-purple),#7c3aed)",display:"flex",alignItems:"center",justifyContent:"center",boxShadow:"0 4px 32px rgba(96,81,255,.6)" }}>
            <IoPlaySharp size={30} color="#fff" style={{ marginLeft: 3 }} />
          </div>
        </div>
      )}
      <div className="fn-center-flash">
        <div className={`fn-flash-ring${showFlash ? " active" : ""}`}>{flashIcon}</div>
      </div>
      <div className={`fn-controls${ctrlVis ? " vis" : ""}`} onClick={(e) => e.stopPropagation()}>
        <div
          className="fn-seekbar"
          onClick={onSeekbarClick}
          onMouseMove={onSeekbarMouseMove}
          onMouseLeave={() => setSeekTip(null)}
          onMouseDown={() => { seekDragRef.current = true; }}
          onMouseUp={() => { seekDragRef.current = false; }}
        >
          <div className="fn-seekbar-track">
            <div className="fn-seekbar-buf" style={{ width: `${buffered * 100}%` }} />
            <div className="fn-seekbar-prog" style={{ width: `${progress * 100}%` }} />
            <div className="fn-seekbar-thumb" style={{ left: `${progress * 100}%` }} />
          </div>
          {seekTip && <div className="fn-seek-tip" style={{ left: seekTip.x }}>{fmtTime(seekTip.time)}</div>}
        </div>
        <div className="fn-ctrl-row">
          <button className="fn-cbtn lg" onClick={togglePlay}>{playing ? <IoPauseSharp /> : <IoPlaySharp />}</button>
          <button className="fn-cbtn sm" onClick={(e) => { e.stopPropagation(); if (videoRef.current) videoRef.current.currentTime = Math.max(0, currentTime - 10); }}><IoPlaySkipBackSharp /></button>
          <button className="fn-cbtn sm" onClick={(e) => { e.stopPropagation(); if (videoRef.current) videoRef.current.currentTime = Math.min(duration, currentTime + 10); }}><IoPlaySkipForwardSharp /></button>
          <div className="fn-vol" onClick={(e) => e.stopPropagation()}>
            <button className="fn-cbtn" onClick={toggleMute}><VolumeIcon /></button>
            <div className="fn-vol-slide-wrap">
              <input className="fn-vol-input" type="range" min="0" max="1" step="0.05" value={muted ? 0 : volume}
                onChange={(e) => changeVolume(+e.target.value)} />
            </div>
          </div>
          <div className="fn-time">{fmtTime(currentTime)}<span className="fn-time-sep"> / </span>{fmtTime(duration)}</div>
          <div style={{ flex: 1 }} />
          <div style={{ position:"relative" }} onClick={(e) => e.stopPropagation()}>
            <button className="fn-cbtn sm"
              onClick={() => { setShowSpeedMenu(s => !s); setShowQualMenu(false); }}
              style={{ gap:2,width:"auto",padding:"0 8px",borderRadius:8,fontSize:".74rem",fontWeight:800,color:speed!==1?"var(--fn-p)":"#fff" }}>
              <IoSpeedometerOutline size={15} /><span style={{ fontSize:".72rem",fontWeight:800 }}>{speed}×</span>
            </button>
            {showSpeedMenu && (
              <div className="fn-speed-menu">
                <div className="fn-menu-hdr">Velocidade</div>
                {SPEEDS.map(s => (
                  <div key={s} className={`fn-speed-item${speed===s?" active":""}`}
                    onClick={() => { changeSpeed(s); setShowSpeedMenu(false); }}>
                    <span>{s===1?"Normal":`${s}×`}</span>{speed===s && <IoCheckmarkSharp />}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div style={{ position:"relative" }} onClick={(e) => e.stopPropagation()}>
            <button className="fn-cbtn sm"
              onClick={() => { setShowQualMenu(s => !s); setShowSpeedMenu(false); }}
              style={{ gap:2,width:"auto",padding:"0 8px",borderRadius:8,fontSize:".72rem",fontWeight:800,color:quality!=="auto"?"var(--fn-p)":"#fff" }}>
              <IoLayers size={15} /><span style={{ fontSize:".72rem",fontWeight:800 }}>{quality==="auto"?"Auto":quality+"p"}</span>
            </button>
            {showQualMenu && (
              <div className="fn-qual-menu">
                <div className="fn-menu-hdr">Qualidade</div>
                {QUALITIES.map(q => (
                  <div key={q.value} className={`fn-speed-item${quality===q.value?" active":""}`}
                    onClick={() => { setQuality(q.value); setShowQualMenu(false); }}>
                    <span>{q.label}</span>{quality===q.value && <IoCheckmarkSharp />}
                  </div>
                ))}
              </div>
            )}
          </div>
          <button className="fn-cbtn sm"
            onClick={(e) => { e.stopPropagation(); setShowSpeedMenu(false); setShowQualMenu(false); }}>
            <IoSettingsSharp style={{ animation: showSpeedMenu||showQualMenu?"fnSpin .6s linear infinite":"none" }} />
          </button>
          {onExpand && (
            <button className="fn-cbtn sm" onClick={(e) => { e.stopPropagation(); onExpand(); }}><IoExpandSharp /></button>
          )}
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   SIMPLE RICH TEXT EDITOR
══════════════════════════════════════════════════════════════ */
const SimpleEditor = ({ value, onChange, placeholder }) => {
  const editorRef = useRef(null);

  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (el.innerHTML !== (value || "")) {
      el.innerHTML = value || "";
    }
  }, [value === ""]);

  const execCmd = (cmd, val = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
  };

  const handleInput = () => {
    const html = editorRef.current?.innerHTML || "";
    const clean = html === "<br>" || html === "<div><br></div>" ? "" : html;
    onChange(clean);
  };

  const toolbarBtns = [
    { cmd: "bold",               label: <strong>B</strong>, title: "Negrito" },
    { cmd: "italic",             label: <em>I</em>,         title: "Itálico" },
    { cmd: "underline",          label: <u>U</u>,           title: "Sublinhado" },
    { cmd: "insertUnorderedList",label: "•—",               title: "Lista" },
    { cmd: "insertOrderedList",  label: "1.",               title: "Lista numerada" },
  ];

  return (
    <div className="fn-simple-editor">
      <div className="fn-se-toolbar">
        {toolbarBtns.map(b => (
          <button key={b.cmd} type="button" className="fn-se-btn" title={b.title}
            onMouseDown={e => { e.preventDefault(); execCmd(b.cmd); }}>
            {b.label}
          </button>
        ))}
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        className="fn-se-body"
        data-placeholder={placeholder || "Escreve aqui…"}
        onInput={handleInput}
        onBlur={handleInput}
      />
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   COMMENT ITEM
══════════════════════════════════════════════════════════════ */
const CommentItem = ({ c, postId, users, meUser, onDeleted, socket, depth = 0 }) => {
  const [showReply, setShowReply] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [subs, setSubs] = useState(c.subcomments || []);
  const [liked, setLiked] = useState(false);
  const [likes, setLikes] = useState(+(c.likes || 0));
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!socket) return;
    const ev = `subcomment:${c.ed_comment_id}`;
    socket.on(ev, (sub) => setSubs(p => [...p, sub]));
    return () => socket.off(ev);
  }, [socket, c.ed_comment_id]);

  const submitReply = async () => {
    if (!replyText.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(
        Hoot() + "api/feednews/subcomments",
        { post_id: postId, parent_comment_id: c.ed_comment_id, content: replyText },
        { withCredentials: true }
      );
      setReplyText(""); setShowReply(false);
    } catch { toast.error("Erro ao responder."); }
    setSubmitting(false);
  };

  const toggleLike = async () => {
    setLiked(l => !l);
    setLikes(l => liked ? l - 1 : l + 1);
    try {
      await axios.post(Hoot() + `api/feednews/comments/${c.ed_comment_id}/like`, {}, { withCredentials: true });
    } catch {}
  };

  const canDelete = meUser && (meUser.id === c.ed_comment_user_id);
  const deleteComment = async () => {
    if (!window.confirm("Eliminar comentário?")) return;
    try {
      await axios.delete(Hoot() + `api/feednews/comments/${c.ed_comment_id}`, { withCredentials: true });
      onDeleted?.(c.ed_comment_id);
    } catch { toast.error("Erro."); }
  };

  const isSub = depth > 0;
  const BubbleClass = isSub ? "fn-subcmt-bubble" : "fn-cmt-bubble";

  return (
    <div className={isSub ? "fn-subcmt" : "fn-cmt"}>
      <Avatar name={c.author_name} pic={c.author_pic} size={isSub ? 28 : 34} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className={BubbleClass}>
          <span className="fn-cmt-author">{c.author_name || "Utilizador"}</span>
          <span className="fn-cmt-time">{fmtRelative(c.ed_comment_created_at)}</span>
          <div className="fn-cmt-text" dangerouslySetInnerHTML={{ __html: c.ed_comment_content }} />
        </div>
        <div className="fn-cmt-actions">
          <button className={`fn-cmt-act${liked ? " liked" : ""}`} onClick={toggleLike}>
            {liked ? <IoHeart /> : <IoHeartOutline />}
            {likes > 0 && <span>{likes}</span>}
          </button>
          {!isSub && (
            <button className="fn-cmt-act" onClick={() => setShowReply(s => !s)}>
              <IoReturnDownForwardOutline /> Responder
            </button>
          )}
          {canDelete && (
            <button className="fn-cmt-act delete" onClick={deleteComment}><IoTrashOutline /></button>
          )}
        </div>
        {showReply && (
          <div className="fn-comment-box" style={{ marginTop: 8 }}>
            <Mentions
              className="fn-mentions-input"
              placeholder={`@ Responder a ${c.author_name}…`}
              value={replyText}
              onChange={setReplyText}
              prefix="@"
              rows={2}
            >
              {(users || []).map(u => <Mentions.Option key={u.id} value={u.name}>{u.name}</Mentions.Option>)}
            </Mentions>
            <div style={{ display:"flex",justifyContent:"flex-end",gap:8 }}>
              <button className="fn-cancel-btn" onClick={() => setShowReply(false)}>Cancelar</button>
              <button className="fn-send-btn" onClick={submitReply} disabled={submitting || !replyText.trim()}>
                {submitting ? <Spinner size="sm" animation="border" /> : <><IoSendSharp /> Enviar</>}
              </button>
            </div>
          </div>
        )}
        {subs.length > 0 && (
          <div className="fn-subcmts">
            {subs.map(s => (
              <CommentItem key={s.ed_comment_id} c={s} postId={postId} users={users}
                meUser={meUser} socket={socket} depth={depth + 1} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   COMMENTS SECTION
══════════════════════════════════════════════════════════════ */
const CommentsSection = ({ postId, users, meUser, socket }) => {
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(Hoot() + `api/feednews/posts/${postId}/comments`, { withCredentials: true });
      setComments(r.data.data || []);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [postId]);
  useEffect(() => {
    if (!socket) return;
    socket.on(`comment:${postId}`, (c) => setComments(p => [...p, c]));
    return () => socket.off(`comment:${postId}`);
  }, [socket, postId]);

  const submit = async () => {
    if (!text.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(Hoot() + "api/feednews/comments", { post_id: postId, content: text }, { withCredentials: true });
      setText("");
    } catch { toast.error("Erro ao comentar."); }
    setSubmitting(false);
  };

  return (
    <div className="fn-comments">
      <div className="fn-comment-header">
        <div className="fn-comment-title">
          <IoChatbubble size={16} color="var(--fn-p)" />
          Comentários
          {comments.length > 0 && <span className="fn-comment-cnt">{comments.length}</span>}
        </div>
      </div>
      <div className="fn-comment-compose">
        <Avatar name={meUser?.name} pic={meUser?.picture} size={36} />
        <div className="fn-comment-box">
          <Mentions
            className="fn-mentions-input"
            placeholder="Escreve um comentário… usa @ para mencionar"
            value={text}
            onChange={setText}
            prefix="@"
            rows={2}
          >
            {(users || []).map(u => <Mentions.Option key={u.id} value={u.name}>{u.name}</Mentions.Option>)}
          </Mentions>
          <div className="fn-comment-foot">
            <span style={{ fontSize:".72rem",color:"var(--fn-muted2)",fontWeight:500 }}>@ mencionar · suporta formatação</span>
            <button className="fn-send-btn" onClick={submit} disabled={submitting || !text.trim()}>
              {submitting ? <Spinner size="sm" animation="border" /> : <><IoSendSharp /> Comentar</>}
            </button>
          </div>
        </div>
      </div>
      {loading && [1,2].map(i => (
        <div key={i} style={{ display:"flex",gap:10,marginBottom:12 }}>
          <div className="fn-skel" style={{ width:34,height:34,borderRadius:"50%",flexShrink:0 }} />
          <div style={{ flex:1 }}><div className="fn-skel" style={{ height:60 }} /></div>
        </div>
      ))}
      {!loading && comments.map(c => (
        <CommentItem key={c.ed_comment_id} c={c} postId={postId} users={users}
          meUser={meUser} socket={socket}
          onDeleted={(id) => setComments(p => p.filter(x => x.ed_comment_id !== id))} />
      ))}
      {!loading && comments.length === 0 && (
        <div style={{ textAlign:"center",padding:"16px 0",color:"var(--fn-muted2)",fontSize:".83rem",fontWeight:500 }}>
          Sem comentários ainda. Sê o primeiro a comentar!
        </div>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   POST CARD
══════════════════════════════════════════════════════════════ */
const PostCard = ({ post, meUser, users, onDeleted, socket }) => {
  const [showComments, setShowComments] = useState(false);
  const [liked, setLiked] = useState(!!post.user_liked);
  const [likes, setLikes] = useState(+(post.likes_count || 0));
  const [showVideoModal, setShowVideoModal] = useState(false);

  const isEvent = post.ed_post_type === "event";
  const videoUrl = post.ed_post_video_url || post.video_url;
  const ytVideoId = videoUrl ? ytId(videoUrl) : null;
  const images = useMemo(() =>
    (post.files || []).filter(f =>
      f.ed_file_type?.startsWith("image/") || /\.(jpg|jpeg|png|gif|webp)$/i.test(f.ed_file_url || "")
    ), [post.files]);
  const imgUrls = images.map(f => f.ed_file_url);
  const canDelete = meUser && (meUser.id === post.ed_post_user_id);

  const toggleLike = async () => {
    setLiked(l => !l);
    setLikes(l => liked ? l - 1 : l + 1);
    try {
      await axios.post(Hoot() + `api/feednews/posts/${post.ed_post_id}/like`, {}, { withCredentials: true });
    } catch {}
  };

  const deletePost = async () => {
    if (!window.confirm("Eliminar este post?")) return;
    try {
      await axios.delete(Hoot() + `api/feednews/posts/${post.ed_post_id}`, { withCredentials: true });
      onDeleted?.(post.ed_post_id);
      toast.success("Post eliminado.");
    } catch { toast.error("Erro ao eliminar."); }
  };

  const eventDate = isEvent && post.ed_post_event_date ? new Date(post.ed_post_event_date) : null;
  const DAYS   = ["Dom","Seg","Ter","Qua","Qui","Sex","Sáb"];
  const MONTHS = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];

  return (
    <div className="fn-post fn-card">
      <div className="fn-post-header">
        <div className="fn-post-meta">
          <Avatar name={post.author_name} pic={post.author_pic} size={42} />
          <div>
            <div className="fn-post-name">
              {post.author_name || "Utilizador"}
              {isEvent && <span className="fn-pill fn-pill-event"><IoCalendarOutline size={10} /> Evento</span>}
            </div>
            <div className="fn-post-time">{fmtRelative(post.ed_post_created_at)}</div>
          </div>
        </div>
        <div style={{ display:"flex",gap:4,alignItems:"center" }}>
          {canDelete && (
            <button className="fn-dots" onClick={deletePost} title="Eliminar post">
              <IoTrashOutline />
            </button>
          )}
          <button className="fn-dots"><IoEllipsisHorizontal /></button>
        </div>
      </div>

      {isEvent && eventDate && (
        <div className="fn-event-block">
          <div className="fn-event-datecol">
            <div className="fn-event-mon">{MONTHS[eventDate.getMonth()]}</div>
            <div className="fn-event-day">{eventDate.getDate()}</div>
            <div className="fn-event-dow">{DAYS[eventDate.getDay()]}</div>
          </div>
          <div className="fn-event-info">
            <div className="fn-event-name">{post.ed_post_event_title || post.ed_post_title}</div>
            {post.ed_post_event_location && (
              <div className="fn-event-row"><IoLocationOutline /> {post.ed_post_event_location}</div>
            )}
            {post.ed_post_event_time && (
              <div className="fn-event-row"><IoTimeOutline /> {post.ed_post_event_time}</div>
            )}
          </div>
        </div>
      )}

      {post.ed_post_content && (
        <div className="fn-post-body" dangerouslySetInnerHTML={{ __html: post.ed_post_content }} />
      )}

      {imgUrls.length > 0 && (
        <div className="fn-img-grid"> </div>
      )}

      {videoUrl && !ytVideoId && (
        <VideoPlayer src={videoUrl} onExpand={() => setShowVideoModal(true)} />
      )}

      {ytVideoId && (
        <div className="fn-yt-box">
          <iframe
            src={`https://www.youtube.com/embed/${ytVideoId}?rel=0&modestbranding=1`}
            title="YouTube"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      )}

      <div className="fn-actions">
        <button className={`fn-act${liked ? " liked" : ""}`} onClick={toggleLike}>
          {liked ? <IoHeart /> : <IoHeartOutline />}
          {likes > 0 && <span className="fn-act-n">{likes}</span>}
          <span>{liked ? "Gostei" : "Gosto"}</span>
        </button>
        <div className="fn-act-sep" />
        {/* FIX: use button type="button" to avoid any form submit interference */}
        <button type="button" className={`fn-act${showComments ? " commented" : ""}`}
          onClick={() => setShowComments(s => !s)}>
          {showComments ? <IoChatbubble /> : <IoChatbubbleOutline />}
          <span>Comentar</span>
        </button>
        <div className="fn-act-sep" />
        <button className="fn-act"><IoShareSocialOutline /><span>Partilhar</span></button>
      </div>

      {showComments && (
        <CommentsSection postId={post.ed_post_id} users={users} meUser={meUser} socket={socket} />
      )}

      <Modal show={showVideoModal} onHide={() => setShowVideoModal(false)} size="xl" centered className="fn-modal fn-vmodal">
        <Modal.Header closeButton>
          <Modal.Title>
            <IoVideocamOutline style={{ marginRight:8 }} />
            {post.ed_post_title || post.author_name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {videoUrl && !ytVideoId && <VideoPlayer src={videoUrl} cinema />}
        </Modal.Body>
      </Modal>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   SKELETON
══════════════════════════════════════════════════════════════ */
const SkeletonPost = () => (
  <div className="fn-card" style={{ marginBottom:14,padding:16,overflow:"hidden" }}>
    <div style={{ display:"flex",gap:11,marginBottom:14 }}>
      <div className="fn-skel" style={{ width:42,height:42,borderRadius:"50%",flexShrink:0 }} />
      <div style={{ flex:1 }}>
        <div className="fn-skel" style={{ height:13,width:"38%",marginBottom:7 }} />
        <div className="fn-skel" style={{ height:10,width:"22%" }} />
      </div>
    </div>
    <div className="fn-skel" style={{ height:13,marginBottom:8 }} />
    <div className="fn-skel" style={{ height:13,width:"75%",marginBottom:12 }} />
    <div className="fn-skel" style={{ height:220,borderRadius:12 }} />
  </div>
);

/* ══════════════════════════════════════════════════════════════
   CREATE POST MODAL
══════════════════════════════════════════════════════════════ */
const CreatePostModal = ({ show, onHide, users, meUser, onCreated }) => {
  const [type, setType] = useState("post");
  const [content, setContent] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [ytPreviewId, setYtPreviewId] = useState(null);
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [videoFile, setVideoFile] = useState(null);
  const [eventTitle, setEventTitle] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [eventTime, setEventTime] = useState("");
  const [eventLocation, setEventLocation] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const imgRef = useRef();
  const vidRef = useRef();

  const MAX_IMG = 3 * 1024 * 1024;
  const MAX_VID = 20 * 1024 * 1024;
  const VALID_IMG = ["image/jpeg","image/png","image/gif","image/webp"];

  const reset = () => {
    setType("post"); setContent(""); setVideoUrl(""); setYtPreviewId(null);
    setFiles([]); setPreviews([]); setVideoFile(null);
    setEventTitle(""); setEventDate(""); setEventTime(""); setEventLocation("");
  };

  const handleImages = (e) => {
    const sel = Array.from(e.target.files || []);
    const valid = sel.filter(f => {
      if (!VALID_IMG.includes(f.type)) { toast.error(`Formato inválido: ${f.name}`); return false; }
      if (f.size > MAX_IMG) { toast.error(`Imagem excede 3MB: ${f.name}`); return false; }
      return true;
    });
    setFiles(p => [...p, ...valid]);
    setPreviews(p => [...p, ...valid.map(f => URL.createObjectURL(f))]);
  };

  const handleVideo = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_VID) { toast.error("Vídeo excede 20MB."); return; }
    setVideoFile(f);
  };

  useEffect(() => { setYtPreviewId(ytId(videoUrl)); }, [videoUrl]);

  const submit = async () => {
    if (!content.trim() && type !== "event") { toast.error("O post precisa de conteúdo."); return; }
    if (type === "event" && !eventTitle.trim()) { toast.error("Título do evento obrigatório."); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("post_type", type);
      fd.append("content", content);
      if (videoUrl) fd.append("video_url", videoUrl);
      if (type === "event") {
        fd.append("event_title", eventTitle);
        fd.append("event_date", eventDate);
        fd.append("event_time", eventTime);
        fd.append("event_location", eventLocation);
      }
      files.forEach(f => fd.append("images", f));
      if (videoFile) fd.append("video", videoFile);
      const r = await axios.post(Hoot() + "api/feednews/posts", fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      onCreated?.(r.data.data);
      reset(); onHide();
      toast.success("Post publicado! 🎉");
    } catch (e) { toast.error(e?.response?.data?.msg || "Erro ao publicar."); }
    setSubmitting(false);
  };

  return (
    <Modal
      show={show}
      onHide={() => { if (!submitting) { reset(); onHide(); } }}
      size="lg"
      centered
      className="fn-modal"
    >
      <Modal.Header closeButton={!submitting}>
        <Modal.Title><IoAddSharp size={20} color="var(--ed-purple)" /> Nova Publicação</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="fn-type-tabs">
          <button className={`fn-type-tab${type==="post"?" active":""}`} onClick={() => setType("post")}>
            <IoImageOutline /> Publicação
          </button>
          <button className={`fn-type-tab${type==="event"?" active":""}`} onClick={() => setType("event")}>
            <IoCalendarOutline /> Evento
          </button>
        </div>
        <div style={{ display:"flex",gap:11,alignItems:"center",marginBottom:16 }}>
          <Avatar name={meUser?.name} pic={meUser?.picture} size={42} />
          <div>
            <div style={{ fontWeight:800,fontSize:".9rem",color:"#0c1225" }}>{meUser?.name || "Você"}</div>
            <div style={{ fontSize:".73rem",color:"#9aaabb",fontWeight:500,marginTop:1 }}>Publicar agora para a equipa</div>
          </div>
        </div>
        {type === "event" && (
          <div className="fn-event-fields">
            <div className="fn-event-fields-hdr"><IoCalendarOutline /> Detalhes do Evento</div>
            <div style={{ display:"flex",flexDirection:"column",gap:8 }}>
              <input className="fn-input" placeholder="Título do evento *" value={eventTitle} onChange={e => setEventTitle(e.target.value)} />
              <div className="fn-grid2">
                <input type="date" className="fn-input" value={eventDate} onChange={e => setEventDate(e.target.value)} />
                <input type="time" className="fn-input" value={eventTime} onChange={e => setEventTime(e.target.value)} />
              </div>
              <input className="fn-input" placeholder="Local ou link" value={eventLocation} onChange={e => setEventLocation(e.target.value)} />
            </div>
          </div>
        )}
        <div style={{ marginBottom:14 }}>
          <SimpleEditor value={content} onChange={setContent} placeholder="O que queres partilhar com a equipa?" />
        </div>
        {previews.length > 0 && (
          <div className="fn-thumb-row">
            {previews.map((p, i) => (
              <div key={i} className="fn-thumb">
                <img src={p} alt="" />
                <button className="fn-thumb-del" onClick={() => {
                  setFiles(f => f.filter((_,idx) => idx!==i));
                  setPreviews(v => v.filter((_,idx) => idx!==i));
                }}><IoCloseSharp /></button>
              </div>
            ))}
          </div>
        )}
        {videoFile && (
          <div className="fn-vid-pill">
            <IoVideocamOutline />
            <strong style={{ flex:1 }}>{videoFile.name}</strong>
            <span style={{ color:"#637087",fontSize:".74rem" }}>{(videoFile.size/1024/1024).toFixed(1)} MB</span>
            <button style={{ background:"none",border:"none",cursor:"pointer",color:"#637087",display:"flex" }} onClick={() => setVideoFile(null)}>
              <IoCloseSharp size={16} />
            </button>
          </div>
        )}
        <div style={{ marginBottom:8,marginTop:10 }}>
          <input className="fn-input" placeholder="URL YouTube (opcional)" value={videoUrl} onChange={e => setVideoUrl(e.target.value)} />
          {ytPreviewId && (
            <div className="fn-yt-prev">
              <iframe src={`https://www.youtube.com/embed/${ytPreviewId}`} title="Preview"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
            </div>
          )}
        </div>
        <div style={{ display:"flex",gap:6,marginTop:12,flexWrap:"wrap" }}>
          <button className="fn-cmp-act" onClick={() => imgRef.current?.click()}><IoImageOutline /> Imagens</button>
          {!videoUrl && (
            <button className="fn-cmp-act" onClick={() => vidRef.current?.click()}><IoVideocamOutline /> Vídeo (máx 20MB)</button>
          )}
          <input ref={imgRef} type="file" multiple accept="image/jpeg,image/png,image/gif,image/webp" className="fn-hidden" onChange={handleImages} />
          <input ref={vidRef} type="file" accept="video/*" className="fn-hidden" onChange={handleVideo} />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button className="fn-cancel-btn" onClick={() => { reset(); onHide(); }} disabled={submitting}>Cancelar</button>
        <button className="fn-send-btn" onClick={submit} disabled={submitting}>
          {submitting ? <Spinner size="sm" animation="border" /> : <><IoSendSharp /> Publicar</>}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

/* ══════════════════════════════════════════════════════════════
   MAIN — FEEDNEWS
══════════════════════════════════════════════════════════════ */
const FeedNews = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [meUser, setMeUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [socket, setSocket] = useState(null);
  const sentinelRef = useRef(null);
  const loadingRef  = useRef(false);
  const LIMIT = 8;

  /* ── socket ── */
  useEffect(() => {
    try {

      const link = Hoot().replace(/\/$/, "")
     
        const s = io(link, {
          path: "/socket/",
          withCredentials: true,
          transports: ["websocket"]
        });

  
      s.on("connect", () => console.log("[FeedNews] socket ✓"));
      s.on("post:new", (p) => setPosts(prev => {
        if (prev.some(x => x.ed_post_id === p.ed_post_id)) return prev;
        return [p, ...prev];
      }));
      s.on("post:deleted", ({ postId }) => setPosts(prev => prev.filter(x => x.ed_post_id !== postId)));
      setSocket(s);
      return () => s.disconnect();
    } catch (e) { console.warn("socket:", e); }
  }, []);

  /* ── me ── */
  useEffect(() => {
    axios.get(Hoot() + "me", { withCredentials: true })
      .then(r => setMeUser(r.data?.data || r.data)).catch(() => {});
  }, []);

  /* ── users ── */
  useEffect(() => {
    axios.get(Hoot() + "api/manager-accounts", { withCredentials: true })
      .then(r => setUsers(r.data || [])).catch(() => {});
  }, []);

  /* ── load posts ── */
  const loadPosts = async (pageNum = 1, append = false) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    if (pageNum === 1) setLoading(true); else setLoadingMore(true);
    try {
      const r = await axios.get(
        Hoot() + `api/feednews/posts?page=${pageNum}&limit=${LIMIT}`,
        { withCredentials: true }
      );
      const data = r.data.data || [];
      if (append) setPosts(p => [...p, ...data]); else setPosts(data);
      setHasMore(data.length === LIMIT);
    } catch {
      toast.error("Erro ao carregar feed.");
      setHasMore(false); // CRITICAL: stop infinite scroll on error (CORS, 502, etc.)
    } finally {
      setLoading(false);
      setLoadingMore(false);
      loadingRef.current = false;
    }
  };

  useEffect(() => { loadPosts(1); }, []);

  /* ── infinite scroll sentinel ── */
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && hasMore && !loadingRef.current) {
        setPage(prev => {
          const next = prev + 1;
          loadPosts(next, true);
          return next;
        });
      }
    }, { threshold: 0.4 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore]);

  /* ── FIX: open modal handler — guaranteed click via dedicated handler ── */
  const openCreate = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setShowCreate(true);
  };

  return (
    <>
      <style>{CSS}</style>
      <div className="fn-root">
        <div className="fn-bg-wrap">
          <div className="fn-layout">

            {/* ── FEED ── */}
            <div>
              <div className="fn-card fn-composer">
                {/* FIX: onClick moved to button-level elements, not just div wrapper */}
                <div className="fn-cmp-row" onClick={openCreate} role="button" tabIndex={0}
                  onKeyDown={e => e.key === "Enter" && openCreate(e)}>
                  <Avatar name={meUser?.name} pic={meUser?.picture} size={42} />
                  <div className="fn-cmp-fake">O que queres partilhar com a equipa?</div>
                </div>
                <div className="fn-cmp-actions">
                  <button type="button" className="fn-cmp-act" onClick={openCreate}>
                    <IoImageOutline /> Foto
                  </button>
                  <button type="button" className="fn-cmp-act" onClick={openCreate}>
                    <IoVideocamOutline /> Vídeo
                  </button>
                  <button type="button" className="fn-cmp-act" onClick={openCreate}>
                    <IoCalendarOutline /> Evento
                  </button>
                </div>
              </div>

              {loading
                ? [1,2,3].map(i => <SkeletonPost key={i} />)
                : posts.map(p => (
                  <PostCard key={p.ed_post_id} post={p} meUser={meUser} users={users} socket={socket}
                    onDeleted={(id) => setPosts(prev => prev.filter(x => x.ed_post_id !== id))} />
                ))
              }

              <div ref={sentinelRef} style={{ height: 1 }} />
              {loadingMore && (
                <div style={{ display:"flex",justifyContent:"center",padding:20 }}>
                  <Spinner animation="border" size="sm" style={{ color:"var(--fn-p)" }} />
                </div>
              )}
              {!hasMore && !loading && posts.length > 0 && (
                <div className="fn-feed-end">Chegaste ao fim do feed</div>
              )}
            </div>

            {/* ── SIDEBAR ── */}
            <div className="fn-sidebar">
              <div className="fn-card fn-sb-card">
                <div className="fn-sb-title">
                  <span style={{ display:"flex",alignItems:"center",gap:7 }}>
                    <IoPeopleOutline size={16} color="var(--fn-p)" /> Equipa
                  </span>
                  <span style={{ fontSize:".7rem",color:"var(--fn-muted2)",fontWeight:600 }}>{users.length} membros</span>
                </div>
                {users.slice(0, 7).map(u => (
                  <div key={u.id} className="fn-sb-user">
                    <Avatar name={u.name} pic={u.picture || u.photo} size={34} online={Math.random() > .5} />
                    <div style={{ flex:1,minWidth:0 }}>
                      <div className="fn-sb-name" style={{ overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{u.name}</div>
                      <div className="fn-sb-role">{u.role || u.cargo || "Manager"}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="fn-card fn-sb-card" style={{ background:"linear-gradient(135deg,rgba(96,81,255,.09),rgba(124,58,237,.07))",border:"1px solid rgba(96,81,255,.16)" }}>
                <div className="fn-sb-title" style={{ color:"var(--fn-p)" }}>
                  <span style={{ display:"flex",alignItems:"center",gap:7 }}><IoSendSharp size={14} /> Publicar</span>
                </div>
                <p style={{ fontSize:".81rem",color:"var(--fn-muted)",lineHeight:1.6,marginBottom:12 }}>
                  Mantém a equipa informada. Partilha novidades, eventos e atualizações.
                </p>
                <button type="button" className="fn-send-btn" style={{ width:"100%",justifyContent:"center" }} onClick={openCreate}>
                  <IoAddSharp /> Nova publicação
                </button>
              </div>

              <button type="button" className="fn-cmp-act"
                style={{ width:"100%",justifyContent:"center",border:"1px solid var(--fn-border)",borderRadius:"var(--fn-r2)" }}
                onClick={() => { setPage(1); loadPosts(1); }}>
                <IoRefreshSharp /> Atualizar feed
              </button>
            </div>

          </div>{/* fn-layout */}
        </div>{/* fn-bg-wrap */}
      </div>{/* fn-root */}

      {/* FIX: Modal rendered OUTSIDE fn-root so Bootstrap portal works without contain:style interference */}
      <CreatePostModal
        show={showCreate}
        onHide={() => setShowCreate(false)}
        users={users}
        meUser={meUser}
        onCreated={(newPost) => { if (newPost) setPosts(p => [newPost, ...p]); }}
      />
    </>
  );
};

export default FeedNews;